import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import {
  GsStructCheckoutComponent,
  GsStructHeader,
  GsStructInquiryCheckoutComponent,
  ICheckoutItem,
  ICheckoutMethods,
  ICheckoutPickupChainstore,
  ICheckoutRender,
  ICheckoutUpdateItemResponse,
  IContactAddress,
  IMethod,
  IPaginated,
  IPaginatedList,
  IPaymentMethod,
  IResult,
  ISetCreateUpdateAddressResponse,
  ISetMethodResponse,
  IShippingMethod,
  ITotalRow,
  TransactionType
} from '../../models';
import { MethodsService } from '../methods/methods.service';
import { debounceTime, Observable, Subject, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { ComponentProvider } from '../../providers';

/** Idle time in ms before changes queued during a running item update are sent. */
const QUEUED_CHANGES_DELAY = 500;

@Injectable({
  providedIn: 'root'
})
export class CheckoutService {
  // Services
  private readonly methodsService = inject(MethodsService);
  private readonly componentService: ComponentProvider = inject(ComponentProvider);
  private readonly destroyRef = inject(DestroyRef);

  // Properties & Signals
  /** Indicates if the checkout data is currently being loaded/saved */
  isLoading: WritableSignal<boolean> = signal(false);
  /** All positions listed in the sum-up-list inside the checkout */
  totalRows: WritableSignal<ITotalRow[]> = signal([]);
  /** Single result entry, set from sources that emit one message (e.g. immediate operation responses). */
  result: WritableSignal<IResult | undefined> = signal(undefined);
  /** Result array, set from sources that emit a list (cart render, batched responses, error payloads). */
  resultArray: WritableSignal<IResult[] | undefined> = signal(undefined);
  /**
   * Combined view of {@link result} and {@link resultArray} — the single
   * result is prepended to the array. Always returns an array (empty when
   * nothing is set), so consumers can use `.length` for visibility checks.
   * A truthy check on the raw signal would otherwise see an empty array as
   * truthy and render an empty result box, causing a layout gap.
   */
  results: Signal<IResult[]> = computed(() => {
    const single = this.result();
    const array = this.resultArray();

    if (single && array?.length) {
      return [single, ...array];
    }

    if (single) {
      return [single];
    }

    return array ?? [];
  });
  /** Basket has blocking errors (a `ResultError` entry is present) — ordering is not possible */
  readonly hasErrors: Signal<boolean> = computed(() => this.results().some((result) => result.resultType === 'ResultError'));
  /** List of available shipping addresses */
  shippingAddress: WritableSignal<IPaginatedList<IContactAddress> | undefined> = signal(undefined);
  /** List of available invoice addresses */
  invoiceAddress: WritableSignal<IPaginatedList<IContactAddress> | undefined> = signal(undefined);
  /** Available delivery methods */
  deliveryMethods: WritableSignal<IPaginatedList<IShippingMethod> | undefined> = signal(undefined);
  /** Available payment methods */
  paymentMethods: WritableSignal<IPaginatedList<IPaymentMethod> | undefined> = signal(undefined);
  /** Available shipping methods */
  shippingMethods: WritableSignal<IPaginatedList<IShippingMethod> | undefined> = signal(undefined);
  /** Available pickup methods */
  pickupMethods: WritableSignal<IPaginatedList<IShippingMethod> | undefined> = signal(undefined);
  /** Available pickup chainstores */
  pickupChainstores: WritableSignal<IPaginatedList<ICheckoutPickupChainstore> | undefined> = signal(undefined);
  /** Current checkout methods */
  checkoutMethods: WritableSignal<ICheckoutMethods | undefined> = signal(undefined);
  /** Items in the checkout */
  items: WritableSignal<IPaginated<ICheckoutItem> | undefined> = signal(undefined);
  /** The complete checkout cart */
  checkoutCart: WritableSignal<ICheckoutRender | undefined> = signal(undefined);
  /**
   * The CMS-configured checkout component without its render, sent as `code` header on every
   * reload. Without it the backend renders a default component and drops settings like "Versandart ausblenden".
   */
  component: WritableSignal<GsStructCheckoutComponent | GsStructInquiryCheckoutComponent | undefined> = signal(undefined);
  /** Backend endpoint the render is (re)fetched from — the inquiry checkout has its own */
  readonly componentType: Signal<'Checkout' | 'InquiryCheckout'> = computed(() => this.component()?.componentType ?? 'Checkout');
  /** Indicates that an item update is currently in progress. */
  private readonly updatingItem: WritableSignal<boolean> = signal(false);
  /** Latest requested item update per item id, queued while another item update is running. */
  private readonly _queuedItemUpdates: Map<number, ICheckoutItem> = new Map();
  /** Latest requested transaction type per item id, queued while another item update is running. */
  private readonly _queuedTransactionTypes: Map<number, TransactionType> = new Map();
  /** Triggers sending of the queued changes, debounced so a burst of changes results in one request. */
  private readonly _flushQueued: Subject<void> = new Subject<void>();

  constructor() {
    // Wait for the changes to settle before sending what was queued during a running request,
    // otherwise every further change would go out back to back with the requests completing.
    this._flushQueued.pipe(debounceTime(QUEUED_CHANGES_DELAY), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.sendQueued());
  }

  // Methods
  /**
   * Initializes the checkout data from the backend render object.
   * @param render The checkout render object from the backend.
   */
  initData(render: ICheckoutRender) {
    this.checkoutCart.set(render);
    this.totalRows.set(render.totalRows);
    this.resultArray.set(render.results);
    this.shippingAddress.set(render.shippingAddress);
    this.invoiceAddress.set(render.invoiceAddress);
    this.paymentMethods.set(render.paymentMethod);
    this.deliveryMethods.set(render.deliveryMethod);
    this.shippingMethods.set(render.shippingMethod);
    this.pickupMethods.set(render.pickupMethod);
    this.pickupChainstores.set(render.pickupChainstore);
    this.checkoutMethods.set(render.methods);
    this.items.set(render.items);
  }

  /**
   * Sets the shipping address by id.
   * @param addressId  The id of the address to set as shipping.
   * @param method The method to call for setting the shipping address.
   */
  setShippingAddress(addressId: number, method: IMethod) {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.methodsService.callMethod<ISetCreateUpdateAddressResponse>(method, null, null, { id: addressId }).subscribe({
      next: (response) => {
        if (!response) {
          this.isLoading.set(false);
          return;
        }
        this.shippingAddress.update(() => response.shippingAddress);
        this.totalRows.update(() => response.totalRows);
        this.resultArray.set(response.results);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error setting shipping address:', error);
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Sets the invoice address by id.
   * @param addressId  The id of the address to set as invoice.
   * @param method The method to call for setting the invoice address.
   */
  setInvoiceAddress(addressId: number, method: IMethod) {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.methodsService.callMethod<ISetCreateUpdateAddressResponse>(method, null, null, { id: addressId }).subscribe({
      next: (response) => {
        if (!response) {
          this.isLoading.set(false);
          return;
        }
        this.invoiceAddress.update(() => response.invoiceAddress);
        this.totalRows.update(() => response.totalRows);
        this.resultArray.set(response.results);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error setting invoice address:', error);
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Sets the delivery method by id.
   * @param deliveryMethodID  The id of the delivery method to set.
   * @param method The method to call for setting the delivery method.
   */
  setDeliveryMethod(deliveryMethodID: number, method: IMethod) {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.methodsService.callMethod<ISetMethodResponse>(method, null, null, { id: deliveryMethodID }).subscribe({
      next: (response) => {
        if (!response) {
          this.isLoading.set(false);
          return;
        }
        this.deliveryMethods.update(() => response.deliveryMethod);
        this.totalRows.update(() => response.totalRows);
        this.resultArray.set(response.results);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error setting delivery method:', error);
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Sets the payment method by id.
   * @param paymentMethodID  The id of the payment method to set.
   * @param method The method to call for setting the payment method.
   */
  setPaymentMethod(paymentMethodID: number, method: IMethod) {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.methodsService.callMethod<ISetMethodResponse>(method, null, null, { id: paymentMethodID }).subscribe({
      next: (response) => {
        if (!response) {
          this.isLoading.set(false);
          return;
        }
        this.paymentMethods.update(() => response.paymentMethod);
        this.totalRows.update(() => response.totalRows);
        this.resultArray.set(response.results);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error setting payment method:', error);
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Sets the pickup method by id.
   * @param pickupMethodID  The id of the pickup method to set.
   * @param method The method to call for setting the pickup method.
   */
  setPickupMethod(pickupMethodID: number, method: IMethod) {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.methodsService.callMethod<ISetMethodResponse>(method, null, null, { id: pickupMethodID }).subscribe({
      next: (response) => {
        if (!response) {
          this.isLoading.set(false);
          return;
        }
        this.pickupMethods.update(() => response.pickupMethod);
        this.totalRows.update(() => response.totalRows);
        this.resultArray.set(response.results);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error setting pickup method:', error);
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Sets the shipping method by id.
   * @param shippingMethodID  The id of the shipping method to set.
   * @param method The method to call for setting the shipping method.
   */
  setShippingMethod(shippingMethodID: number, method: IMethod) {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.methodsService.callMethod<ISetMethodResponse>(method, null, null, { id: shippingMethodID }).subscribe({
      next: (response) => {
        if (!response) {
          this.isLoading.set(false);
          return;
        }
        this.shippingMethods.update(() => response.shippingMethod);
        this.totalRows.update(() => response.totalRows);
        this.resultArray.set(response.results);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error setting shipping method:', error);
        this.isLoading.set(false);
      }
    });
  }

  /**
   * Sets the pickup chain store by id.
   * @param chainStoreID  The id of the pickup chain store to set.
   * @param method The method to call for setting the pickup chain store.
   */
  setPickupChainStore(chainStoreID: number, method: IMethod) {
    if (this.isLoading()) return;
    this.isLoading.set(true);
    this.methodsService.callMethod<ISetMethodResponse>(method, null, null, { id: chainStoreID }).subscribe({
      next: (response) => {
        if (!response) {
          this.isLoading.set(false);
          return;
        }
        this.pickupChainstores.update(() => response.pickupChainstore);
        this.totalRows.update(() => response.totalRows);
        this.resultArray.set(response.results);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error setting pickup chain store:', error);
        this.isLoading.set(false);
      }
    });
  }

  updateCart(): Observable<ICheckoutRender> {
    return this.componentService
      .get<
        ICheckoutRender,
        GsStructHeader<GsStructCheckoutComponent | GsStructInquiryCheckoutComponent | undefined>
      >(this.componentType(), { code: this.component() })
      .pipe(
        tap((response) => {
          if (!response) return;

          this.initData(response);
        })
      );
  }

  /**
   * Call to the backend to update the item in the shoppingcart
   * @param item The item that should be updated
   */
  updateItem(item: ICheckoutItem) {
    // Queue instead of sending right away, so item updates never overlap - the backend answers
    // a concurrent write on the same basket with a row version conflict.
    // Only the newest state per item is kept - it carries the absolute quantity anyway.
    if (this.updatingItem()) {
      this._queuedItemUpdates.set(item.iD, item);
      return;
    }
    this.result.set(undefined);
    this.resultArray.set(undefined);
    const currentCart = this.checkoutCart();
    const selectedShippingId = this.shippingMethods()?.selected?.iD;

    if (!currentCart?.methods.update) return;

    const body = {
      ...item,
      shippingMethodID: selectedShippingId
    };

    this.updatingItem.set(true);
    this.methodsService
      .callMethod<ICheckoutUpdateItemResponse>(currentCart.methods.update, null, body, { id: item.iD })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (!response) {
            this.finishItemUpdate();
            return;
          }

          // Apply what the answer already carries, so the totals don't wait for the reload below
          if (response.totalRows) {
            this.totalRows.set(response.totalRows);
          }

          if (response.shippingMethod) {
            this.shippingMethods.set({
              ...response.shippingMethod,
              selected: response.shippingMethod.selected ?? this.shippingMethods()?.selected
            });
          }

          this.resultArray.set(response.results);

          // The update endpoint answers without items, so reload the checkout to pick up
          // recalculated item prices (e.g. tiered prices reacting to the new quantity)
          this.reloadCart(response.results, 'item update');
        },
        error: (error) => {
          if (error.error?.results && error.error.results.length > 0) {
            this.resultArray.set(error.error.results);
          }
          this.finishItemUpdate();
        }
      });
  }

  /**
   * Sets the transaction type for a checkout item.
   * @param type The transaction type to set.
   * @param itemId The ID of the checkout item to update.
   */
  setTransactionType(type: TransactionType, itemId: number) {
    // Queue instead of sending right away, see updateItem - both write the same basket
    if (this.updatingItem()) {
      this._queuedTransactionTypes.set(itemId, type);
      return;
    }
    const checkoutMethods = this.checkoutMethods();
    if (!checkoutMethods?.setTransactionType) return;

    this.updatingItem.set(true);
    this.methodsService
      .callMethod<ISetMethodResponse>(checkoutMethods.setTransactionType, { type }, null, { id: itemId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (!response) {
            this.finishItemUpdate();
            return;
          }
          this.totalRows.update(() => response.totalRows);
          this.resultArray.set(response.results);

          // The answer carries neither the pickup/shipping methods nor the pickup chainstore, so
          // reload the checkout - otherwise switching to Click & Collect leaves both stale.
          this.reloadCart(response.results, 'transaction type change');
        },
        error: () => this.finishItemUpdate()
      });
  }

  /**
   * Reloads the render after a write and marks the item update as done.
   * @param results Messages of the write - the reload overwrites them with the render's, so they are restored
   * @param context What was written, for the error log
   */
  private reloadCart(results: IResult[] | undefined, context: string) {
    this.updateCart()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.resultArray.set(results),
        error: (error) => {
          console.error(`Error refreshing checkout after ${context}:`, error);
          this.finishItemUpdate();
        },
        complete: () => this.finishItemUpdate()
      });
  }

  /**
   * Marks the running item update as done and schedules the queued changes, if any.
   */
  private finishItemUpdate() {
    this.updatingItem.set(false);

    if (this._queuedTransactionTypes.size > 0 || this._queuedItemUpdates.size > 0) this._flushQueued.next();
  }

  /**
   * Sends the longest waiting queued change, carrying its newest state. Further queued changes
   * follow once this one finished.
   */
  private sendQueued() {
    if (this.updatingItem()) return; // A new request already started, its completion schedules the rest

    const [nextType] = this._queuedTransactionTypes.entries();
    if (nextType) {
      const [itemId, type] = nextType;
      this._queuedTransactionTypes.delete(itemId);
      this.setTransactionType(type, itemId);
      return;
    }

    const [nextItem] = this._queuedItemUpdates.values();
    if (!nextItem) return;
    this._queuedItemUpdates.delete(nextItem.iD);
    this.updateItem(nextItem);
  }

  /** Call to the backend to delete the item from the checkout cart */
  deleteItem(id: number) {
    const checkoutMethods = this.checkoutMethods();
    if (!checkoutMethods?.delete) return;
    this.methodsService.callMethod<ICheckoutItem>(checkoutMethods.delete, null, null, { id }).subscribe({
      next: () => {
        this.updateCart().subscribe();
      },
      error: (error) => {
        console.error('Error deleting item from checkout cart:', error);
      }
    });
  }
}
